import React, { useState } from 'react'
import LiveBusRouteMap from './LiveBusRouteMap'
import { Location, useLocation } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { fetchDailyTrips } from '../liveTrip/api'
import { useQueries } from 'react-query'
import { Helmet } from 'react-helmet'
import { Processing } from '../../../components/Loaders'
import { GeoJSONFeature, toGeoJSON } from '../../../helpers/toGeoJSON'
import { fetchSchoolDetails } from '../profile/api'
import clsx from 'clsx'
import { BusLocationData, DailyTripAPIData } from '../../ts/trip'
import TripDetails from './TripDetails'

// Use the exact return type of toGeoJSON for defaults
type GeoJSONCollection = ReturnType<typeof toGeoJSON>

interface SchoolDetails {
  school_location_latitude?: number
  school_location_longitude?: number
  [key: string]: any
}

const liveTripBreadcrumbs = [
  {
    title: 'Live Trips',
    path: '/trip/live-trip',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

// Default geojson matching the shape produced by toGeoJSON
const DEFAULT_GEOJSON: GeoJSONCollection = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [0, 0],
      },
      properties: {},
    } as GeoJSONFeature,
  ],
}

/**
 * DEFAULT_BUS_DATA is a safe fallback. We don't know every property of BusLocationData here,
 * so cast using `as unknown as BusLocationData` to satisfy the compiler while still providing
 * a sensible geoJson and a few safe defaults. When the API returns trips we spread the actual
 * selectedBus so all required fields are present at runtime.
 */
const DEFAULT_BUS_DATA = {
  geoJson: DEFAULT_GEOJSON as unknown as BusLocationData['geoJson'],
  daily_trip_id: null,
  bus_id: null,
  bus_name: '',
  bus_number: '',
  driver_name: '',
  bus_registration_number: '',
} as unknown as BusLocationData

const BusLiveLocation: React.FC = () => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const formattedDate = `${year}-${month}-${day}`

  // location.state may be undefined or missing properties — guard it
  const location = useLocation() as Location & { state?: GeoJSONFeature }
  const selectedBusGeoJSONFeature = location?.state

  const [lookAtBus, setLookAtBus] = useState<boolean>(true)

  const results = useQueries([
    {
      queryKey: ['busLocations', formattedDate],
      queryFn: () => fetchDailyTrips(formattedDate),
      refetchOnWindowFocus: false,
      select: (data: any) => {
        // Normalize API response to an array (handles axios-like { data: [...] } or plain [])
        const trips: DailyTripAPIData[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : []

        // If there are no trips, return the default Bus data
        if (trips.length === 0) {
          return DEFAULT_BUS_DATA
        }

        // Pick registration from location state safely
        const selectedRegistration = selectedBusGeoJSONFeature?.properties?.bus_registration_number

        // If no registration provided, fall back to the first trip (so component has valid shape)
        const chosen =
          (selectedRegistration && trips.find((b) => b?.bus_registration_number === selectedRegistration)) ||
          trips[0]

        if (!chosen) {
          // Extremely defensive: if something strange happens, return default
          return DEFAULT_BUS_DATA
        }

        // Build geoJson using helper (matches GeoJSONCollection)
        const geoJson = toGeoJSON([chosen], (loc) => ({
          daily_trip_id: loc.daily_trip_id,
          bus_id: loc.bus_id,
          bus_name: loc.bus_name,
          bus_number: loc.bus_number,
          driver_name: loc.driver_name,
          bus_registration_number: loc.bus_registration_number,
        }))

        // Spread all fields from the chosen trip so BusLocationData-required fields are present,
        // and add/override geoJson with the generated one.
        const result = {
          ...chosen,
          geoJson: geoJson as unknown as BusLocationData['geoJson'],
        } as BusLocationData

        return result
      },
      refetchInterval: 2000,
    },
    {
      queryKey: ['school_details'],
      queryFn: fetchSchoolDetails,
      refetchOnWindowFocus: false,
    },
  ])

  // defensive loading check
  if (!results || results.length < 2 || results[0].isLoading || results[1].isLoading) {
    return <Processing />
  }

  // coerce to safe objects
  const busLocationData = (results[0]?.data ?? DEFAULT_BUS_DATA) as BusLocationData
  const schoolDetails = results[1]?.data?.data as SchoolDetails

  // debug logs (outside JSX)
  // console.debug('bus results:', results[0]?.data)
  // console.debug('busLocationData:', busLocationData)

  return (
    <>
      <Helmet>
        <title>TrackNAB | Bus live location</title>
      </Helmet>

      <PageTitle breadcrumbs={liveTripBreadcrumbs}>
        {busLocationData && busLocationData.bus_name
          ? `${busLocationData.bus_name} - ${busLocationData.bus_registration_number}`
          : 'Bus live location'}
      </PageTitle>

      <div className="d-flex">
        <div style={{ width: '70%' }}>
          <LiveBusRouteMap
            // pass the geoJson object (not a string). If LiveBusRouteMap expects a special class,
            // you can adjust casting here.
            busLiveLocation={busLocationData?.geoJson ?? DEFAULT_GEOJSON}
            schoolLocation={{
              lat: schoolDetails?.school_location_latitude,
              lng: schoolDetails?.school_location_longitude,
            }}
            lookAtBus={lookAtBus}
            setLookAtBus={setLookAtBus}
          />
        </div>

        <div style={{ width: '30%' }}>
          <div className="bg-white h-100 rounded-end-4 px-5 py-3 position-relative">
            <span
              role="button"
              onClick={() => setLookAtBus(true)}
              className="position-absolute bg-white p-1 opacity-75"
              style={{ right: '100%', top: 0 }}
            >
              <i className={clsx('las la-crosshairs fs-3hx', lookAtBus && 'text-primary')}></i>
            </span>

            <TripDetails tripData={busLocationData} />
          </div>
        </div>
      </div>
    </>
  )
}

export default BusLiveLocation
